import { Paper, Stack, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { OrderTableSummaryStyled } from "Components/CreateJob/CreateJob.styled";
import { IBundle, PaymentResponse } from "Components/CreateJob/CreateJobModel";
import { useNotification } from "Components/Notification";
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { ShBullets } from "shared/SharedStyles/ShDataDisplay";
import { ShButton, ShGreenBtn } from "shared/SharedStyles/ShInputs";
import { areAllElementsInArrayXInArrayY, areSomeElementsInArrayXInArrayY } from "shared/functions";
import { purchaseJob, resetPurchase } from "store/slices/employer/create-job/create-job-payments-slice";

export const Payment = () => {

  const dispatch = useAppDispatch();
  const notification = useNotification();
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { allBundles, inventoryBundles, totalJobCost, selectedBundles, purchaseJobStatus, orderId,
    purchaseJobResponse, purchaseUrl } = useAppSelector((state) => state.employer.createJobPayments);
  const pathSegments = useLocation().pathname.split('/');
  const lastPathSegment = pathSegments[pathSegments.length - 1] as PaymentResponse;

  const payForJob = () => {
    if (orderId) {
      dispatch(purchaseJob({ jobId: parseInt(jobId ?? ''), orderId: orderId }));
    }
  };

  // Show snackbar notifications when the purchase is either successful or failed.
  useEffect(() => {
    if (purchaseJobStatus === 'failed' || purchaseJobStatus === 'success') {
      notification.displayNotification({
        open: true,
        type: purchaseJobStatus === 'failed' ? 'error' : 'success',
        message: purchaseJobResponse ?? ''
      });
      dispatch(resetPurchase());
      // Route to Active Jobs once job is successfully posted.
      if (purchaseJobStatus === 'success') {
        navigate('/employer/jobs/active');
      }
    } else if (purchaseJobStatus === 'stripe_payment_required' && purchaseUrl) {
      window.location.href = purchaseUrl;
    }
  }, [dispatch, navigate, notification, purchaseJobResponse, purchaseJobStatus, purchaseUrl]);

  const getPricingLabel = (bundle: IBundle) => {
    const bundleInInventory = inventoryBundles?.find(ib => ib.bundle_id === bundle.id);
    return (<>
      {
        bundleInInventory ?
          <Typography variant='body1' fontWeight={600}>
            {`1 of ${bundleInInventory?.quantity} 
            credit${bundleInInventory?.quantity > 1 ? 's' : ''}`}
          </Typography> :
          <Typography variant='body1' fontWeight={600}>${bundle.base_price}</Typography>
      }
    </>);
  };


  return (
    <Stack rowGap={1} maxWidth={700}>
      <Typography align='center' variant="body1" fontWeight={600} gutterBottom>Order Summary</Typography>
      <TableContainer component={Paper} variant="outlined">
        <OrderTableSummaryStyled size="small" aria-label="Payment summary table">
          <TableHead>
            <TableRow>
              <TableCell><Typography variant="body1" fontWeight={"bold"} >Quantity</Typography></TableCell>
              <TableCell><Typography variant="body1" fontWeight={"bold"}>Bundle</Typography></TableCell>
              <TableCell><Typography variant="body1" fontWeight={"bold"}>Price</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allBundles?.filter(b => selectedBundles.includes(b.id)).map(bundle => (
              <TableRow className="rowStyle" key={bundle.id}>
                <TableCell className="no-left-border" align="center">1</TableCell>
                <TableCell>
                  <Typography gutterBottom variant="subtitle1" fontWeight={"bold"}>
                    {bundle.name}
                  </Typography>
                  {/* 
                      Bundle description is being sent as '-' separated string from the API.
                      Split it with '-' and display as a Bullet points.
                    */}
                  <ShBullets>
                    {bundle.description.split('-').map((d, i) => (
                      d &&
                      <li key={i}>
                        <Typography variant='caption'>
                          {d}
                        </Typography>
                      </li>
                    ))}
                  </ShBullets>
                </TableCell>
                <TableCell>
                  {/* 
                      If bundles are already available in inventory show the credits deducted label.
                      If no bundles, then display bundle price.
                    */}
                  {getPricingLabel(bundle)}
                </TableCell>
              </TableRow>
            ))}

            {/* Total rows */}
            <TableRow>
              <TableCell rowSpan={3} className="no-border" />
              <TableCell className="no-border" colSpan={1} align="right">Total</TableCell>
              <TableCell className="totalAmountCell">${totalJobCost}</TableCell>
            </TableRow>

            {/* Disable */}
            {/* <TableRow>
              <TableCell className="no-border" align="right">Taxes</TableCell>
              <TableCell>${Math.round(totalJobCost * TaxRate)}</TableCell>
            </TableRow> */}

            {/* <TableRow>
              <TableCell className="no-border" align="right">Total</TableCell>
              <TableCell className="totalAmountCell">${totalJobCost}</TableCell>
            </TableRow> */}

          </TableBody>
        </OrderTableSummaryStyled>
      </TableContainer>
      <Outlet context={lastPathSegment === 'success' || purchaseJobStatus === 'success'} />
      <Stack>
        {
          (lastPathSegment !== 'success' && lastPathSegment !== 'failed') &&
          ((areAllElementsInArrayXInArrayY(selectedBundles, inventoryBundles.map(ib => ib.bundle_id)) &&
            areSomeElementsInArrayXInArrayY(selectedBundles, inventoryBundles.map(ib => ib.bundle_id))) ?
            <Typography variant="body2" gutterBottom>You are about to use some of the credits for this Job</Typography> :
            <Typography variant="body2" gutterBottom>Looks like you don't have enough credits for this Job with the selected bundles.
              Proceed to purchase?</Typography>)
        }
        <Stack paddingTop='10px' flexDirection='row' justifyContent='space-between'>
          <ShButton variant='contained' size='small' disableElevation
            onClick={() => navigate(`/employer/job/${jobId}/bundles`)}>Back</ShButton>
          {
            lastPathSegment === 'success' ?
              <ShGreenBtn className="self-right" variant="contained" color="primary"
                onClick={() => navigate('/employer/jobs/active')}>
                Go to My Jobs
              </ShGreenBtn> :
              <ShGreenBtn className="self-right" onClick={payForJob}>{
                purchaseJobStatus === 'pending' ? 'Processing...' :
                  lastPathSegment === 'failed' ? 'Retry' : 'Confirm'}</ShGreenBtn>
          }
        </Stack>
      </Stack>
    </Stack>
  );
};